// In App.js in a new project
import 'react-native-reanimated';
import 'react-native-gesture-handler';
import ConsentModalContent from '@/components/SharedComponents/ConsentModalContent/ConsentModalContent';
import { ErrorBoundary } from '@/components/SharedComponents/ErrorBoundary/ErrorBoundary';
import LoadingIndicator from '@/components/SharedComponents/LoadingIndicator/LoadingIndicator';
import constants, { isPatientApp } from '@/config/constants';
import { useStoreRehydrated } from 'easy-peasy';
import { Audio } from 'expo-av';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Linking, Platform, StatusBar, StyleSheet, View } from 'react-native';
import { StatusBar as ExpoStatusBar } from 'expo-status-bar';
import 'react-native-get-random-values';
import {
  addSslPinningErrorListener,
  isSslPinningAvailable,
} from 'react-native-ssl-public-key-pinning';
import Toast from 'react-native-toast-message';
import api from './api/axios';
import { postConsents } from './api/services/consents';
import { localLogoutUser } from './api/services/user';
import { checkAppVersion } from './api/services/versions';
import { Layout } from './layouts/Layout';
import { AppNavigation } from './navigations/AppNavigation';
import { navigate } from './navigations/RootNavigation';
import { getAccessToken, serverLogoutUser } from './stores/auth/services';
import store, { useStoreState } from './stores/store';
import { startCheckConsent } from './utils/consents';
import sslPinning from './utils/sslPinning';
import { initTrackers } from './utils/utils';

import { NativeWindStyleSheet } from 'nativewind';
import { ConfirmDialog } from './components/ui/shared/Dialog';
import { useFont } from './hooks/useFont';
import { useStartupChecks } from './hooks/useStartupChecks';
import { logError } from './utils/logHandling';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { init as initSentry, setUser, wrap } from '@sentry/react-native';
import { IS_WEB, IS_ANDROID, IS_IOS } from './utils/platform';

initSentry({
  dsn:
    constants.sentryDsn ||
    'https://ca75223dfbbfa00355e00bd6f7bb7b42@sentry.neolexon.de/4509357247627344',
  sendDefaultPii: false,
  enabled: constants.sentryEnabled,
  environment: constants.environment,
  release: constants.appVersion,
  enableLogs: constants.sentryEnabled,
  initialScope: {
    tags: {
      appMode: constants.appMode,
    },
  },
});

const storeName = IS_ANDROID ? 'Play Store' : IS_IOS ? 'App Store' : '';

const appEnvironment = constants.appMode as 'therapist' | 'patient';

const appLinks = {
  therapist: {
    ios: 'itms-apps://apps.apple.com/de/app/neolexon-aphasie/id1197504761',
    android: 'market://details?id=com.neolexon.therapeut', //ask for link on Android?
  },
  patient: {
    ios: 'itms-apps://apps.apple.com/de/app/neolexon-aphasie/id1169666941',
    android: 'market://details?d=com.ionicframework.patientapp613489', // link on Android?
  },
};

// Function to handle opening the store based on the platform and environment
const openStore = () => {
  let url = '';

  if (IS_ANDROID) {
    url = appLinks[appEnvironment].android;
  } else if (IS_IOS) {
    url = appLinks[appEnvironment].ios;
  }
  if (url) {
    Linking.openURL(url).catch((err) => {
      logError('Failed to open URL:', err);
    });
  }
};

initTrackers(Platform.OS);
// initialize SSL Pinning
sslPinning.init();

function MainApp() {
  useStartupChecks();
  useFont();
  const [isLoading, setIsLoading] = useState(false);
  const [interceptorsAdded, setInterceptorsAdded] = useState(false);
  const [showNoConnectionModal, setShowNoConnectionModal] = useState(false);
  const [forceCloseConsentModal, setForceCloseConsentModal] = useState(false);

  const {
    showAppNeedToBeUpdatedModal,
    showCriticalAppUpdateModal,
    showConsentModal,
    showLoadingSpinner,
    isTherapistDemo,
    isPatientDemo,
    signedInUser,
  } = useStoreState((state) => state);
  const { showNotification } = useNotification();
  const { setShowLoadingSpinner } = store.getActions();

  const checkStatus = useCallback(async () => {
    try {
      // Check for app new version
      await checkAppVersion();
    } catch (error) {
      logError('Could not get app version', error);
    }
  }, []);

  useLayoutEffect(() => {
    StatusBar.setHidden(true);
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    if (isLoading) {
      timeoutId = setTimeout(() => {
        setShowLoadingSpinner(true);
      }, 300);
    } else {
      timeoutId = setTimeout(() => {
        // Show loading spinner for 500ms to avoid flickering
        setShowLoadingSpinner(false);
      }, 500);
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    const userId = signedInUser.user?._id;
    if (userId) {
      setUser({ id: userId });
    }
  }, [signedInUser]);

  useEffect(() => {
    // Interceptors for HTTP requests
    // Request interceptors
    Audio.setAudioModeAsync({ playsInSilentModeIOS: true });
    if (isSslPinningAvailable()) {
      addSslPinningErrorListener(() => {
        showNotification({
          title: 'SSL Pinning Fehler',
          type: 'error',
          duration: 1000 * 60 * 60,
        });
        // Triggered when an SSL pinning error occurs due to pin mismatch
        // logInfo('pinning error: ' + error.serverHostname + ' PLATFORM ' + Platform.OS);
      });
    }
    const request = api.interceptors.request.use(async (config) => {
      // ignore this API calls because it is too slow or unnecessary to block the UI for it
      let APIS_EXCLUDED_FORM_LOADING_SPINNER = [
        '/api/auth/reset',
        '/api/words/search',
        '/api/sentences/search',
        '/api/texts/search',
        '/api/consent',
        !isPatientApp ? '/api/permissions' : '',
      ];

      if (!APIS_EXCLUDED_FORM_LOADING_SPINNER.includes(config.url ?? '')) {
        setIsLoading(true);
      }

      // Add token for each request except sign in
      let EXCLUDED_APIS = ['/api/auth/signin'];
      if (
        !EXCLUDED_APIS.includes(config.url ?? '') &&
        store.getState().isAuthenticated
      ) {
        const accessToken = await getAccessToken();
        config.headers['Authorization'] = 'JWT ' + accessToken;
      }
      return config;
    });

    // Response Interceptors
    const response = api.interceptors.response.use(
      (response) => {
        setIsLoading(false);
        return response;
      },
      async (error) => {
        // Handle 403 response
        if (
          error?.response?.status === 403 ||
          error?.response?.status === 401
        ) {
          showNotification({
            title: 'Sitzung abgelaufen. Bitte melden Sie sich neu an.',
            type: 'error',
          });
          await serverLogoutUser({ jwt: (await getAccessToken()) ?? '' });
          await localLogoutUser();
          store.getActions().setStayLoggedIn(false);
          navigate('Login');
        }

        // Handle error responses
        setIsLoading(false);
        return Promise.reject(error);
      },
    );

    setInterceptorsAdded(true);
    checkStatus();

    return () => {
      api.interceptors.request.eject(request);
      api.interceptors.response.eject(response);
    };
  }, []);

  const showConsentConfrmAction = async () => {
    try {
      await postConsents(showConsentModal?.document);
      store.getActions().setShowConsentModal({
        document: '',
        buttonText: '',
        show: false,
      });
      startCheckConsent();
    } catch (error) {
      logError('Could not post consent', error);
      setForceCloseConsentModal(true);
      setTimeout(() => {
        setShowNoConnectionModal(true);
      }, 500);
      store.getActions().setShowConsentModal({
        document: '',
        buttonText: '',
        show: false,
      });
    }
  };

  return (
    <>
      <Layout>
        {/* Consent Modal */}
        {!isTherapistDemo &&
        !isPatientDemo &&
        showConsentModal?.buttonText !== '' &&
        showConsentModal?.show ? (
          <ConfirmDialog
            isVisible={showConsentModal?.show ? !forceCloseConsentModal : false}
            confirmText={showConsentModal?.buttonText}
            confirmAction={showConsentConfrmAction}
          >
            <ConsentModalContent
              consentText={showConsentModal?.document?.uri}
            />
          </ConfirmDialog>
        ) : null}
        {/* No connection modal */}
        <ConfirmDialog
          isVisible={showNoConnectionModal}
          headerText={'Verbindungsfehler'}
          confirmText={'Schließen'}
          contentText={
            'Bitte überprüfen Sie Ihre Internetverbindung und versuchen es erneut.'
          }
          confirmAction={() => {
            setShowNoConnectionModal(false);
            setForceCloseConsentModal(false);
          }}
        />
        {/* App version is outdated modal */}
        <ConfirmDialog
          isVisible={showAppNeedToBeUpdatedModal}
          headerText={'App-Update verfügbar'}
          contentText={
            IS_WEB
              ? 'Bitte aktualisieren Sie Ihre neolexon App'
              : `Bitte aktualisieren Sie Ihre neolexon App über den ${storeName}`
          }
          confirmText={IS_WEB ? 'Aktualisieren' : '@/store öffnen'}
          cancelText={'Abbrechen'}
          confirmAction={IS_WEB ? () => window.location.reload() : openStore}
          cancelAction={() => {
            store.getActions().setShowAppNeedToBeUpdatedModal(false);
          }}
        />
        <ConfirmDialog
          isVisible={showCriticalAppUpdateModal}
          headerText={'App-Update erforderlich'}
          contentText={
            IS_WEB
              ? 'Bitte aktualisieren Sie Ihre neolexon App'
              : `Bitte aktualisieren Sie Ihre neolexon App über den ${storeName}`
          }
          confirmText={IS_WEB ? 'Aktualisieren' : '@/store öffnen'}
          confirmAction={IS_WEB ? () => window.location.reload() : openStore}
        />
        <ExpoStatusBar hidden={true} />
        <LoadingIndicator
          showLoading={showLoadingSpinner || !interceptorsAdded}
        />
        {interceptorsAdded ? <AppNavigation /> : null}
        <Toast topOffset={70} />
      </Layout>
    </>
  );
}

function App() {
  const AppSpinner = useMemo(() => {
    return (
      <View style={styles.container}>
        <LoadingIndicator showLoading />
      </View>
    );
  }, []);

  const isRehydrated = useStoreRehydrated();
  const showMainApp = isRehydrated;
  return (
    <ErrorBoundary root>{showMainApp ? <MainApp /> : AppSpinner}</ErrorBoundary>
  );
}

// expose store when run in Cypress
if (window.Cypress) {
  window.store = store;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default wrap(App);

NativeWindStyleSheet.setOutput({
  default: 'native',
});
